import { useAppConfig } from '@plato/app-core/hooks/use-app-config';
import { useIsCustomerMode } from '@plato/app-core/hooks/use-customer-mode';
import { SalesMetricBasis } from '@plato/app-meta/sales-metric';
import { useCurrencyHelpers } from '@plato/app-shared/hooks/use-currency-helpers';
import {
    formatPercentage,
    PercentageOrEmpty,
} from '@plato/ui-legacy/components/typography/percentage-or-empty';
import { valueFormatter } from '@plato/ui-legacy/lib/chart-helpers';
import { useResolvedLocale } from '@plato/ui-legacy/lib/hooks/locale';
import { TrendChart, TrendChartLegend, type TrendChartPoint } from '@plato/ui/components/trend-chart';
import { SectionCard, SectionCardStickyHeader, SectionCardTitle } from '@plato/ui/containers/section-card';
import { Typography } from '@plato/ui/primitives/typography';
import { cn } from '@plato/ui/util/classes';
import { useMemo, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../../utils/api.ts';
import { ChartLoading } from '../../charts/components/chart-loading.tsx';

type Props = {
    erpCustomerId: string;
};

type GlanceStatProps = {
    label: string;
    value: string;
    percentage?: string;
    delta: number | undefined;
    masked?: boolean;
};

function GlanceStat({ label, value, percentage, delta, masked = false }: GlanceStatProps): ReactElement {
    const isPositive = delta === undefined || delta === 0 ? null : delta > 0;

    return (
        <div className="flex min-w-0 flex-col items-start gap-tight">
            <Typography variant="caption" className="text-content-weak">
                {label}
            </Typography>
            <Typography variant="body" className="tabular-nums">
                {masked ? '*****' : value}
                {!masked && percentage !== undefined && (
                    <span className="font-normal text-content-weak">{` (${percentage})`}</span>
                )}
            </Typography>
            {!masked && delta !== undefined && (
                <span
                    className={cn(
                        'inline-flex shrink-0 items-center rounded-tight px-tight py-px text-ui-sm tabular-nums',
                        isPositive === true && 'bg-green-50 text-green-600',
                        isPositive === false && 'bg-red-50 text-red-600',
                        isPositive === null && 'bg-neutral-50 text-neutral-600',
                    )}
                >
                    <PercentageOrEmpty showPlusSignIfPositive decimals={0}>
                        {delta}
                    </PercentageOrEmpty>
                </span>
            )}
        </div>
    );
}

/**
 * The customer's period at a glance: the tenant's headline metric and gross margin
 * against the same period a year earlier, over a year-on-year trend line.
 *
 * Revenue and order intake are two billing bases of the same measure, and the card has no
 * metric switcher, so `defaultMetric` picks the basis for the stats and the chart alike —
 * the same way the stats bar it replaces does.
 */
export function CustomerDetailGlance({ erpCustomerId }: Props): ReactElement {
    const { t } = useTranslation('customer-detail');
    const locale = useResolvedLocale();
    const { formatCurrency } = useCurrencyHelpers();
    const customerViewToggled = useIsCustomerMode();
    const { canReadGrossMargin, defaultCurrency, defaultMetric, defaultTimeframe } = useAppConfig();

    const defaultBillingStatus =
        defaultMetric === 'Order_Sum' ? SalesMetricBasis.ORDER_SUM : SalesMetricBasis.BILLED;
    const isRollingTimeframe = defaultTimeframe === 'TTM';

    const financials = api.platoUser.getDetailCustomer.getFinancialsWithTimeframe.useQuery({
        erpCustomerId,
        billingStatus: defaultBillingStatus,
        timeframe: defaultTimeframe,
    });

    const filters = useMemo(
        () => ({
            customer: [erpCustomerId],
            clerk: [],
            industry: [],
            product: [],
            productGroup: [],
            storageLocation: [],
            supplier: [],
            salesRep: [],
            normalisedCategory: [],
        }),
        [erpCustomerId],
    );

    const chart = api.platoUser.query.explorer.getExplorerSummary.getChartDataWithTimeframe.useQuery({
        filters,
        billingStatus: defaultBillingStatus,
        timeframe: defaultTimeframe,
    });

    const chartResult = chart.data;

    // `monthInt` is a calendar month, so the month name alone stays unique across both the
    // year-aligned (YTD) and the rolling (TTM) twelve buckets the query returns.
    const chartData = useMemo<TrendChartPoint[]>(
        () =>
            chartResult?.revenueChartData.map((point) => ({
                label: new Date(chartResult.thisYear, point.monthInt - 1, 1).toLocaleString(locale, {
                    month: 'short',
                }),
                current: point.thisYear,
                previous: point.lastYear,
            })) ?? [],
        [chartResult, locale],
    );

    const currentLabel = isRollingTimeframe
        ? t('last_12_months', { ns: 'common' })
        : (chartResult?.thisYear.toString() ?? '');
    const previousLabel = isRollingTimeframe
        ? t('previous_12_months', { ns: 'common' })
        : (chartResult?.lastYear.toString() ?? '');

    return (
        <SectionCard>
            <SectionCardStickyHeader>
                <SectionCardTitle>{t('glance__title')}</SectionCardTitle>
            </SectionCardStickyHeader>
            <div className="grid grid-cols-2 gap-cozy">
                <GlanceStat
                    label={
                        defaultMetric === 'Order_Sum'
                            ? t('stats__decoration_order_sum')
                            : t('stats__decoration_revenue')
                    }
                    value={formatCurrency(financials.data?.total_amount_current)}
                    delta={financials.data?.total_amount_delta_percentage}
                />
                {canReadGrossMargin && (
                    <GlanceStat
                        label={t('stats__decoration_gm')}
                        value={formatCurrency(financials.data?.gross_margin_current)}
                        percentage={
                            financials.data &&
                            formatPercentage(financials.data.gross_margin_percentage, {
                                locale,
                                decimals: 0,
                            })
                        }
                        delta={financials.data?.gross_margin_delta_percentage}
                        masked={customerViewToggled}
                    />
                )}
            </div>

            {chartResult ? (
                <div className="flex flex-col gap-tight">
                    <TrendChart
                        data={chartData}
                        formatValue={(value) => valueFormatter(value, locale, defaultCurrency)}
                        className="h-28"
                    />
                    <TrendChartLegend
                        currentLabel={currentLabel}
                        previousLabel={previousLabel}
                        startLabel={chartData.at(0)?.label}
                        endLabel={chartData.at(-1)?.label}
                    />
                </div>
            ) : (
                <ChartLoading className="h-16" />
            )}
        </SectionCard>
    );
}
